import React, { useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { BirthDatePicker } from '@/components/organisms/customer/_detail/birth-date-picker'
import {
  AccommodationInput,
  GenderType,
  NationalityType,
  OccupationType,
  isEmployee,
  occupationList,
} from '@/models/self-checkin/accommodation-info'
import { fetchZipCode } from '@/apis/aipass'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { FrontTabletSmallCheckbox } from '@/components/molecules/front-tablet/input/small-checkbox'
import { PaxSelect } from '@/components/molecules/pax-select'
import { countryList } from '@/libs/nationality'
import { FrontTabletRadioButton } from '@/components/molecules/front-tablet/input/radio-button'
import { FrontTabletFormLabel } from '@/components/molecules/front-tablet/input/form-label'
import { FrontTabletInputField } from '@/components/molecules/front-tablet/input/input-field'
import { ErrorMessage } from '@/components/molecules/front-tablet/input/error-message'
import { createSelfCheckinResponsiveStyle, FrontTabletResponsiveToken } from '@/utils/front-tablet-responsive-style'

type AccommodationFormProps = {
  accommodationInfo: AccommodationInput
  onChange: (name: any, value: any) => void
  requiredAccompanyNameInput: boolean
  requiredFuriganaInput: boolean
  requiredBirthDateInput: boolean
  requiredGenderInput: boolean
  requiredTelephoneInput: boolean
  requiredEmailInput: boolean
  requiredAddressInput: boolean
  isFormValidRequiredFields: boolean
  imageInfo?: {
    identificationFrontImage: string | undefined
    identificationBackImage: string | undefined
    representativePassportImage: string | undefined
    nationality: string | undefined
    setNationality: (v: string | undefined) => void
    uploadImage: (type: 'front' | 'back' | 'representativePassport') => void
  }
  isEmailValid?: any
  isPhoneValid?: any
  isPostCodeValid?: any
  isFirstNameValid?: any
  isLastNameValid?: any
}

export const AccommodationForm: React.FC<AccommodationFormProps> = ({
  accommodationInfo,
  onChange,
  requiredAccompanyNameInput,
  requiredFuriganaInput,
  requiredBirthDateInput,
  requiredGenderInput,
  requiredTelephoneInput,
  requiredEmailInput,
  requiredAddressInput,
  isFormValidRequiredFields,
  imageInfo,
  isEmailValid,
  isPhoneValid,
  isPostCodeValid,
  isFirstNameValid,
  isLastNameValid,
}) => {
  const [zipFetchLoading, setZipFetchLoading] = useState(false)
  const { t, i18n } = useTranslation()
  const nationalities = countryList(i18n.language, false)

  const lang = i18n.language

  const switchButtonGender = (genderInput: GenderType) => {
    onChange('gender', genderInput)
  }

  const switchButtonNationality = (nationality: NationalityType) => {
    onChange('nationality', nationality)
  }

  const onChangeBirthDate = (value: string): void => {
    onChange('dateOfBirth', value)
  }

  const onChangeAccommodationInfo = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target
    onChange(name, value)
  }

  const _fetchZipCode = async (value: string) => {
    try {
      setZipFetchLoading(true)
      const response = await fetchZipCode(value)
      const result = response?.results[0]
      onChange('address', `${result?.address1}${result?.address2}${result?.address3}`)
    } catch (err) {
      onChange('address', '')
    } finally {
      setZipFetchLoading(false)
    }
  }

  const getName = (lastName, firstName) => {
    switch (true) {
      case !!lastName?.trim() && !!firstName?.trim():
        return lastName + ' ' + firstName
      case !!lastName?.trim():
        return lastName
      case !!firstName?.trim():
        return firstName
      default:
        return ''
    }
  }

  const onChangeAccommodationInfoPax = (value: string): void => {
    onChange('paxTotal', value)
    if (value === '1') {
      onChange('accompany', [])
    } else {
      const selectAccompanyLength = Number(value) - 1
      const stateAccompany = accommodationInfo?.accompany || []
      const diffAccompanyLength = selectAccompanyLength - stateAccompany.length
      if (selectAccompanyLength < 0 || diffAccompanyLength === 0) {
        return
      }
      if (diffAccompanyLength < 0) {
        const reduceAccompany = stateAccompany.slice(0, diffAccompanyLength)
        onChange('accompany', reduceAccompany)
      } else {
        const addAccompany = Array.from(new Array(diffAccompanyLength)).map(() => {
          return { id: uuidv4(), lastName: '', firstName: '', name: '' }
        })
        onChange('accompany', [...stateAccompany, ...addAccompany])
      }
    }
  }

  const onChangeAccommodationInfoAccompanyName = (e, id): void => {
    const { name, value } = e.target
    const stateAccompany = accommodationInfo?.accompany || []
    const changeAccompany = stateAccompany.map(accompany => {
      if (accompany.id === id) {
        const changeLastNameValue = name === 'lastName' ? value : accompany.lastName
        const changeFirstNameValue = name === 'firstName' ? value : accompany.firstName
        const changeNameValue = getName(changeLastNameValue, changeFirstNameValue)
        return {
          id,
          lastName: changeLastNameValue,
          firstName: changeFirstNameValue,
          name: changeNameValue,
        }
      } else {
        return accompany
      }
    })
    onChange('accompany', changeAccompany)
  }

  return (
    <div css={form}>
      {imageInfo && (
        <>
          <p css={formDescriptionStyle}>{t('Representative information')}</p>
          <div css={itemWrapperStyle}>
            {imageInfo.identificationFrontImage && (
              <>
                <FrontTabletFormLabel label={t('Identification')} required />
                <div css={imageItemWrapperStyle}>
                  <div css={imageItemStyle} onClick={() => imageInfo?.uploadImage('front')}>
                    <img
                      src={
                        imageInfo?.identificationFrontImage
                          ? imageInfo?.identificationFrontImage
                          : require('@/static/images/new_identification_card_front.svg')
                      }
                    />
                    <div css={imageItemTextStyle}>
                      <div>
                        <img src={require('@/static/images/camera_icon_white.svg')} alt={t('Front of the card')} />
                        <p css={scan}>{t('Front of the card')}</p>
                      </div>
                    </div>
                  </div>
                  <div css={imageItemStyle} onClick={() => imageInfo?.uploadImage('back')}>
                    <img
                      src={
                        imageInfo?.identificationBackImage
                          ? imageInfo?.identificationBackImage
                          : require('@/static/images/new_identification_card_back.svg')
                      }
                    />
                    <div css={imageItemTextStyle}>
                      <div>
                        <img src={require('@/static/images/camera_icon_white.svg')} alt={t('Back of the card')} />
                        <p css={scan}>{t('Back of the card')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {imageInfo.representativePassportImage && (
              <>
                <FrontTabletFormLabel label={t('Identification')} required />
                <div css={imageItemWrapperStyle}>
                  <div css={imageItemStyle} onClick={() => imageInfo?.uploadImage('representativePassport')}>
                    <img
                      src={
                        imageInfo?.representativePassportImage
                          ? imageInfo?.representativePassportImage
                          : require('@/static/images/passport.svg')
                      }
                    />
                    <div css={imageItemTextStyle}>
                      <div>
                        <img src={require('@/static/images/camera_icon_white.svg')} alt={t('Take a Photo')} />
                        <p css={scan}>{t('Take a Photo')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Residence')} required />
        <div css={radioGroupContainerStyle}>
          {[
            { value: NationalityType.Jp, label: t('Japanese nationality') },
            { value: NationalityType.Njp, label: t('Foreign nationality') },
          ].map(item => (
            <FrontTabletRadioButton
              key={item.value}
              id={`nationality-${item.value}`}
              label={item.label}
              formValue={accommodationInfo?.nationality as NationalityType}
              name="nationality"
              onChange={e => switchButtonNationality(e.target.value as NationalityType)}
              value={item.value}
              radioSizeType="small"
              radioLabelSizeType="small"
              itemCss={getRadioItemStyle()}
            />
          ))}
        </div>
      </div>

      {imageInfo?.representativePassportImage && (
        <div className="reservationLabel">
          <FrontTabletFormLabel label={t('Nationality')} />
          <div css={accompanyNameForm}>
            <select
              css={[assignSelectStyle, css({ width: 200 })]}
              value={imageInfo.nationality}
              onChange={e => imageInfo.setNationality(e.target.value)}
            >
              <option value={''}>{t('Country name')}</option>
              {nationalities.map((country, index) => (
                <option value={country.value} key={index}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Full name')} required />
        <div css={nameForm}>
          <FrontTabletInputField
            type="text"
            className="lastName"
            placeholder={t('Last name')}
            name="lastName"
            value={accommodationInfo?.lastName}
            onChange={onChangeAccommodationInfo}
            error={(!isFormValidRequiredFields && !accommodationInfo?.lastName.trim()) || !isLastNameValid}
          />
          <FrontTabletInputField
            type="text"
            placeholder={t('First name')}
            name="firstName"
            value={accommodationInfo?.firstName}
            error={(!isFormValidRequiredFields && !accommodationInfo?.firstName.trim()) || !isFirstNameValid}
            onChange={onChangeAccommodationInfo}
          />
        </div>
        {!isFormValidRequiredFields && (!accommodationInfo?.lastName.trim() || !accommodationInfo?.firstName.trim()) ? (
          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
        ) : null}
        {!isLastNameValid || !isFirstNameValid ? <ErrorMessage>{t('Please type using half-width characters')}</ErrorMessage> : null}
      </div>
      {accommodationInfo.nationality === NationalityType.Jp && lang === 'ja' && (
        <div className="reservationLabel">
          <FrontTabletFormLabel label={t('Furigana')} required={requiredFuriganaInput} />
          <div css={nameForm}>
            <FrontTabletInputField
              type="text"
              className="lastName"
              placeholder={t('Last name Furigana')}
              name="lastNameFurigana"
              value={accommodationInfo?.lastNameFurigana}
              error={requiredFuriganaInput && !isFormValidRequiredFields && !accommodationInfo?.lastNameFurigana.trim()}
              onChange={onChangeAccommodationInfo}
            />
            <FrontTabletInputField
              type="text"
              placeholder={t('First name Furigana')}
              name="firstNameFurigana"
              value={accommodationInfo?.firstNameFurigana}
              error={requiredFuriganaInput && !isFormValidRequiredFields && !accommodationInfo?.firstNameFurigana.trim()}
              onChange={onChangeAccommodationInfo}
            />
          </div>
          {requiredFuriganaInput &&
          !isFormValidRequiredFields &&
          (!accommodationInfo?.firstNameFurigana.trim() || !accommodationInfo?.lastNameFurigana.trim()) ? (
            <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
          ) : null}
        </div>
      )}
      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Date of birth')} required={requiredBirthDateInput} />
        <div css={[reservationTelForm, birthDatePickerStyle]}>
          <BirthDatePicker
            value={accommodationInfo?.dateOfBirth}
            placeholder={t('Please select')}
            onChange={onChangeBirthDate}
            error={requiredBirthDateInput && !isFormValidRequiredFields && !accommodationInfo?.dateOfBirth}
          />
        </div>
        {requiredBirthDateInput && !isFormValidRequiredFields && !accommodationInfo?.dateOfBirth ? (
          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
        ) : null}
      </div>
      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Gender')} required={requiredGenderInput} />
        <div css={radioGroupContainerStyle}>
          {[
            { value: GenderType.Male, label: t('Male') },
            { value: GenderType.Female, label: t('Female') },
          ].map(item => (
            <FrontTabletRadioButton
              key={item.value}
              id={`gender-${item.value}`}
              label={item.label}
              formValue={accommodationInfo?.gender as GenderType}
              name="gender"
              onChange={e => switchButtonGender(e.target.value as GenderType)}
              value={item.value}
              radioSizeType="small"
              radioLabelSizeType="small"
              itemCss={getRadioItemStyle()}
            />
          ))}
        </div>
      </div>
      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Phone number')} required={requiredTelephoneInput} />
        <div css={reservationTelForm}>
          <FrontTabletInputField
            type="tel"
            placeholder="08012345678"
            pattern="^[0-9]+$"
            error={(requiredTelephoneInput && !isFormValidRequiredFields && !accommodationInfo?.phoneNumber.trim()) || !isPhoneValid}
            name="phoneNumber"
            value={accommodationInfo?.phoneNumber}
            onChange={onChangeAccommodationInfo}
          />
        </div>
        {requiredTelephoneInput && !isFormValidRequiredFields && !accommodationInfo?.phoneNumber.trim() ? (
          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
        ) : null}
        {!isPhoneValid && <ErrorMessage>{t('Please enter numbers only, without symbols or spaces')}</ErrorMessage>}
      </div>
      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Email')} required={requiredEmailInput} />
        <div css={reservationTelForm}>
          <FrontTabletInputField
            type="email"
            placeholder="yamada@aipass.com"
            name="mail"
            error={!isEmailValid || (requiredEmailInput && !isFormValidRequiredFields && !accommodationInfo?.mail.trim())}
            value={accommodationInfo?.mail}
            onChange={onChangeAccommodationInfo}
          />
        </div>
        {requiredEmailInput && !isFormValidRequiredFields && !accommodationInfo?.mail.trim() && (
          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
        )}
        {!isEmailValid && <ErrorMessage>{t('Please enter a valid email address')}</ErrorMessage>}
      </div>
      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Occupation')} />
        <div css={reservationTelForm}>
          <select css={assignSelectStyle} name="occupation" value={accommodationInfo?.occupation} onChange={onChangeAccommodationInfo}>
            {occupationList.map((item, index) => (
              <option key={index} value={item.value}>
                {t(item.name)}
              </option>
            ))}
          </select>
        </div>
        {!isFormValidRequiredFields && !accommodationInfo?.occupation ? (
          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
        ) : null}
      </div>
      {isEmployee(accommodationInfo.occupation) && (
        <div className="reservationLabel">
          <FrontTabletFormLabel label={t('Company')}>
            <span css={companyNoteStyle}>{t('SelfCheckIn.seeCorporateNameReceipt')}</span>
          </FrontTabletFormLabel>
          <div css={reservationTelForm}>
            <FrontTabletInputField
              type="text"
              placeholder={t('Aipass Co Ltd')}
              name="company"
              value={accommodationInfo?.company}
              onChange={onChangeAccommodationInfo}
            />
          </div>
        </div>
      )}
      {accommodationInfo.occupation === OccupationType.Other && (
        <div className="reservationLabel">
          <div css={reservationTelForm}>
            <FrontTabletInputField
              type="text"
              name="otherOccupation"
              value={accommodationInfo?.otherOccupation}
              onChange={onChangeAccommodationInfo}
            />
          </div>
        </div>
      )}
      {accommodationInfo.nationality === NationalityType.Jp && (
        <div className="reservationLabel">
          <FrontTabletFormLabel label={t('Post code')} />
          <div css={[reservationTelForm, postCodeRowStyle]}>
            <FrontTabletInputField
              maxWidth={178}
              type="tel"
              placeholder="1234567"
              pattern="^[0-9]+$"
              name="postCode"
              error={!isPostCodeValid}
              value={accommodationInfo?.postCode}
              onChange={e => {
                onChangeAccommodationInfo(e)
              }}
            />
            <Button
              isDisabled={!accommodationInfo.postCode}
              buttonType={ButtonType.Secondary}
              borderRadius={40}
              marginLeft={20}
              width={140}
              height={62}
              fontSize={18}
              fontWeight="normal"
              loading={zipFetchLoading}
              onClick={() => {
                if (accommodationInfo.postCode) {
                  _fetchZipCode(accommodationInfo?.postCode)
                }
              }}
            >
              {t('Search by Address')}
            </Button>
          </div>
          {!isPostCodeValid && <ErrorMessage>{t('Please enter numbers only, without symbols or spaces')}</ErrorMessage>}
        </div>
      )}
      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Address (Please include street number and building name)')} required={requiredAddressInput} />
        <div css={reservationTelForm}>
          <FrontTabletInputField
            type="text"
            placeholder={t('1-1 Chiyoda Chiyoda-ku')}
            name="address"
            value={accommodationInfo?.address}
            error={requiredAddressInput && !isFormValidRequiredFields && !accommodationInfo?.address.trim()}
            onChange={onChangeAccommodationInfo}
          />
        </div>
        {requiredAddressInput && !isFormValidRequiredFields && !accommodationInfo?.address.trim() ? (
          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
        ) : null}
      </div>

      <div css={deviderStyle} />

      <div className="reservationLabel">
        <FrontTabletFormLabel label={t('Number of guests')} />
        <div css={accompanyPaxForm}>
          <PaxSelect value={`${accommodationInfo?.paxTotal || 1}`} onClick={val => onChangeAccommodationInfoPax(val)}></PaxSelect>
        </div>
        {(accommodationInfo?.paxTotal || 1) > 1 && (
          <>
            {!requiredAccompanyNameInput && (
              <div css={checkBoxStyle}>
                <FrontTabletSmallCheckbox
                  value={accommodationInfo.hasContactForAllGuest}
                  onChange={val => {
                    onChange('hasContactForAllGuest', val)
                    if (!val) {
                      onChangeAccommodationInfoPax(`${accommodationInfo?.paxTotal || 1}`)
                    }
                  }}
                  label={t('has contact information for all guests')}
                />
              </div>
            )}
            <div id="accompanyNameForm">
              {(requiredAccompanyNameInput || !accommodationInfo.hasContactForAllGuest) && (
                <div className="reservationLabel">
                  <div css={accompanyNameForm}>
                    <FrontTabletFormLabel label={t('Accompany name')} required />
                    {(accommodationInfo?.accompany || []).map(accompany => (
                      <div key={accompany?.id} css={nameFormList}>
                        <div css={nameForm}>
                          <FrontTabletInputField
                            type="text"
                            className="lastName"
                            placeholder={t('Last name')}
                            name="lastName"
                            value={accompany?.lastName}
                            error={!isFormValidRequiredFields && !accompany?.lastName?.trim()}
                            onChange={e => onChangeAccommodationInfoAccompanyName(e, accompany?.id)}
                          />
                          <FrontTabletInputField
                            type="text"
                            placeholder={t('First name')}
                            name="firstName"
                            value={accompany?.firstName}
                            error={!isFormValidRequiredFields && !accompany?.firstName?.trim()}
                            onChange={e => onChangeAccommodationInfoAccompanyName(e, accompany?.id)}
                          />
                        </div>
                        {!isFormValidRequiredFields && (!accompany?.lastName?.trim() || !accompany?.firstName?.trim()) ? (
                          <ErrorMessage>{t('Required field has not been entered')}</ErrorMessage>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const form = css(
  {
    width: '100%',
    maxWidth: 'none',

    '.reservationLabel': {
      width: '100%',
      marginBottom: 32,
      padding: 0,
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { '.reservationLabel': { marginBottom: '32.5px' } },
  }),
)

const reservationTelForm = css({
  marginTop: '16px',

  'input::placeholder': {
    color: '#cccccc',
  },
})

const radioGroupContainerStyle = css(
  {
    display: 'flex',
    gap: 32,
    marginTop: 16,
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { marginBottom: -1 },
  }),
)

const getRadioItemStyle = () =>
  css({
    display: 'flex',
    lineHeight: 'initial',
  })

const nameForm = css({
  display: 'flex',
  gap: '2.44%',
  marginTop: '16px',

  '.lastName': {
    marginRight: 0,
  },

  '> input': {
    flex: '1 1 0',
    minWidth: 0,
  },
})

const assignSelectStyle = css(
  {
    width: '100%',
    height: '59px',
    border: '1px solid #cccccc',
    color: '#272727',
    borderRadius: '40px',
    fontSize: '22px',
    fontWeight: 500,
    letterSpacing: '1.1px',
    padding: '14px 30px 15px 35px',
    appearance: 'none',
    backgroundImage: `url(${require('@/static/images/arrow_down_yellow.svg')})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '95% center',
    backgroundSize: 14,
    backgroundColor: '#FFFFFF',
    display: 'block',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      height: '64px',
      fontSize: FrontTabletResponsiveToken.formInputFontSize.landscape13,
      letterSpacing: '1.3px',
    },
  }),
)

const formDescriptionStyle = css(
  {
    fontSize: FrontTabletResponsiveToken.guestFormTitleFontSize.landscape11,
    marginBottom: 24,
    lineHeight: '27px',
    letterSpacing: '1.8px',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: FrontTabletResponsiveToken.guestFormTitleFontSize.landscape13, lineHeight: '36px' },
  }),
)

const itemWrapperStyle = css({
  width: '100%',
  '&:last-child': {
    padding: 0,
  },
})

const imageItemWrapperStyle = css(
  {
    display: 'flex',
    marginTop: 16,
  },
  createSelfCheckinResponsiveStyle({
    portrait: { flexWrap: 'wrap' },
  }),
)

const imageItemStyle = css(
  {
    width: 256,
    height: 158,
    cursor: 'pointer',
    position: 'relative',
    fontSize: 18,
    fontWeight: 'bold',
    letterSpacing: '2.7px',
    lineHeight: '27px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:nth-of-type(1)': {
      margin: '0 20px 20px 0',
    },
    '> img': {
      width: 256,
      height: 158,
      borderRadius: 10,
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      width: 296,
      height: 183,
      fontSize: 22,
      '> img': { width: 296, height: 183 },
    },
  }),
)

const imageItemTextStyle = css({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  margin: 'auto',
  display: 'flex',
  fontSize: 12,
  color: '#ffffff',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  img: {
    width: 19,
    height: 'auto',
  },
  p: {
    color: '#ffffff',
  },
})

const scan = css({
  fontSize: '15px !important',
})

const accompanyPaxForm = css({
  marginTop: '16px',
  width: 'fit-content',
  '> div > div > span': {
    fontSize: '22px !important',
  },
})

const accompanyNameForm = css({
  marginTop: 24,
})

const nameFormList = css({
  marginBottom: 30,
})

const checkBoxStyle = css({
  padding: '24px 32px 0 0',
})

const deviderStyle = css({
  borderTop: '1px solid #F2F2F2',
  margin: '32px 0 32px 0',
})

const birthDatePickerStyle = css(
  {
    input: {
      height: '59px !important',
      fontSize: '22px !important',
      borderRadius: '40px !important',
      fontWeight: 500,
      letterSpacing: '1.1px !important',
      padding: '14px 30px 15px 35px !important',
      border: '1px solid #cccccc !important',
      backgroundSize: '14px !important',
      backgroundPosition: '95% center !important',
    },
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      input: {
        height: '64px !important',
        fontSize: `${FrontTabletResponsiveToken.formInputFontSize.landscape13}px !important`,
        letterSpacing: '1.3px !important',
      },
    },
  }),
)

const companyNoteStyle = css(
  {
    color: '#676767',
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: '0.8px',
    paddingLeft: 16,
  },
  createSelfCheckinResponsiveStyle({
    landscape13: { fontSize: 20, letterSpacing: '1px', paddingLeft: 20 },
  }),
)

const postCodeRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
})
